import React, { useEffect, useState } from "react";
import CommonDivWidget from "components/CommonDivWidget/CommonDivWidget";
import { message, Spin, Row, Col, Switch, Radio, Input } from "antd";
import CommonButton from "components/ButtonWidgets/CommonButton/CommonButton";
import { apiGetSettings, apiSaveSettings } from "services/settingService";

const cardStyle = {
  width: "100%",
  marginTop: 16,
  padding: 24,
  background: "var(--whiteColor)",
  border: "1px solid #eef0f2",
  borderRadius: 12,
};

const labelStyle = { fontFamily: "mediumFont", fontSize: 13, color: "var(--darkGreyColor)", marginBottom: 6 };

// Distinct accents so the active mode is obvious:
// Sandbox = amber (test / caution), Live = green (production / go).
const SANDBOX_ACCENT = "#e0801f";
const SANDBOX_BG = "rgba(246,139,67,0.14)";
const LIVE_ACCENT = "#2f9e57";
const LIVE_BG = "rgba(47,158,87,0.12)";

const credCardStyle = (active, accent, accentBg) => ({
  height: "100%",
  borderRadius: 12,
  padding: 18,
  border: active ? `2px solid ${accent}` : "1px solid #e8eaed",
  background: active ? accentBg : "#fafbfc",
  boxShadow: active ? "0 6px 18px rgba(0,0,0,0.08)" : "none",
  transition: "border-color .2s ease, background-color .2s ease, box-shadow .2s ease",
});

const activeBadge = (bg) => ({
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: 999,
  background: bg,
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
});

const credHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 };
const credTitle = (active) => ({ fontFamily: "mediumFont", fontSize: 15, color: active ? "#1a2c21" : "#9096a0" });

function SettingsPage() {
  const [isBusy, setIsBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const [displayShopping, setDisplayShopping] = useState(true);
  const [paypalMode, setPaypalMode] = useState("sandbox");
  const [sandboxClientId, setSandboxClientId] = useState("");
  const [liveClientId, setLiveClientId] = useState("");
  const [sandboxSecret, setSandboxSecret] = useState("");
  const [liveSecret, setLiveSecret] = useState("");
  const [sandboxSecretSet, setSandboxSecretSet] = useState(false);
  const [liveSecretSet, setLiveSecretSet] = useState(false);

  const applyData = (data) => {
    setDisplayShopping(String(data.display_shopping) === "1");
    setPaypalMode(data.paypal_mode === "live" ? "live" : "sandbox");
    setSandboxClientId(data.paypal_sandbox_client_id || "");
    setLiveClientId(data.paypal_live_client_id || "");
    setSandboxSecret("");
    setLiveSecret("");
    setSandboxSecretSet(!!data.paypal_sandbox_secret_set);
    setLiveSecretSet(!!data.paypal_live_secret_set);
  };

  useEffect(() => {
    setIsBusy(true);
    apiGetSettings()
      .then((data) => {
        setIsBusy(false);
        applyData(data);
      })
      .catch((err) => {
        setIsBusy(false);
        message.error("Sorry! " + err);
      });
  }, []);

  const onSave = () => {
    const settings = {
      display_shopping: displayShopping ? "1" : "0",
      paypal_mode: paypalMode,
      paypal_sandbox_client_id: sandboxClientId,
      paypal_live_client_id: liveClientId,
    };
    // Only send secrets when a new value was typed (blank = keep existing)
    if (sandboxSecret.trim() !== "") settings.paypal_sandbox_secret = sandboxSecret.trim();
    if (liveSecret.trim() !== "") settings.paypal_live_secret = liveSecret.trim();

    setSaving(true);
    apiSaveSettings(settings)
      .then((data) => {
        setSaving(false);
        applyData(data);
        message.success("Settings saved");
      })
      .catch((err) => {
        setSaving(false);
        message.error(err);
      });
  };

  const secretPlaceholder = (isSet) =>
    isSet ? "•••••••• saved — leave blank to keep" : "Enter secret key";

  // The selected mode button takes that mode's accent colour.
  const activeAccent = paypalMode === "live" ? LIVE_ACCENT : SANDBOX_ACCENT;

  return (
    <CommonDivWidget>
      <Spin spinning={isBusy}>
        <h1>Settings</h1>

        {/* Display Shopping */}
        <div style={cardStyle}>
          <Row justify="space-between" align="middle" wrap={false}>
            <Col flex="auto" style={{ paddingRight: 24 }}>
              <div style={{ fontFamily: "mediumFont", fontSize: 16 }}>Display Shopping</div>
              <div style={{ color: "var(--darkGreyColor)", fontSize: 13, marginTop: 4 }}>
                When enabled, the Shop and Cart pages are available on the public website and
                the "Shop" menu is shown. When disabled, those pages and links are hidden and
                purchasing is blocked.
              </div>
            </Col>
            <Col flex="none">
              <Switch
                checkedChildren="On"
                unCheckedChildren="Off"
                checked={displayShopping}
                onChange={setDisplayShopping}
              />
            </Col>
          </Row>
        </div>

        {/* PayPal */}
        <div style={cardStyle}>
          <div style={{ fontFamily: "mediumFont", fontSize: 16, marginBottom: 4 }}>PayPal Payment</div>
          <div style={{ color: "var(--darkGreyColor)", fontSize: 13, marginBottom: 20 }}>
            Choose which environment is live for checkout. Secret keys are encrypted before
            they are stored and are never shown again. Client IDs are public (used by the
            storefront). Changes apply to both the website and the payment backend.
          </div>

          <div style={labelStyle}>Active mode</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <style>{`
              .pp-mode-group .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled),
              .pp-mode-group .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):hover {
                background: ${activeAccent} !important;
                border-color: ${activeAccent} !important;
                color: #fff !important;
                box-shadow: none !important;
              }
              .pp-mode-group .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before {
                background-color: ${activeAccent} !important;
              }
              .pp-mode-group .ant-radio-button-wrapper:not(.ant-radio-button-wrapper-checked):hover {
                color: ${activeAccent};
              }
            `}</style>
            <Radio.Group
              className="pp-mode-group"
              value={paypalMode}
              onChange={(e) => setPaypalMode(e.target.value)}
              buttonStyle="solid"
              size="large"
            >
              <Radio.Button value="sandbox">Sandbox (test)</Radio.Button>
              <Radio.Button value="live">Live</Radio.Button>
            </Radio.Group>
            <span style={{ color: "var(--darkGreyColor)", fontSize: 13 }}>
              Checkout currently uses{" "}
              <strong style={{ color: paypalMode === "live" ? LIVE_ACCENT : SANDBOX_ACCENT }}>
                {paypalMode === "live" ? "LIVE" : "SANDBOX"}
              </strong>{" "}
              credentials.
            </span>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <div style={credCardStyle(paypalMode === "sandbox", SANDBOX_ACCENT, SANDBOX_BG)}>
                <div style={credHeader}>
                  <span style={credTitle(paypalMode === "sandbox")}>Sandbox credentials</span>
                  {paypalMode === "sandbox" && <span style={activeBadge(SANDBOX_ACCENT)}>ACTIVE</span>}
                </div>
                <div style={labelStyle}>Client ID</div>
                <Input
                  value={sandboxClientId}
                  onChange={(e) => setSandboxClientId(e.target.value)}
                  placeholder="Sandbox client id"
                  style={{ marginBottom: 12 }}
                />
                <div style={labelStyle}>Secret key</div>
                <Input.Password
                  value={sandboxSecret}
                  onChange={(e) => setSandboxSecret(e.target.value)}
                  placeholder={secretPlaceholder(sandboxSecretSet)}
                  autoComplete="new-password"
                />
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div style={credCardStyle(paypalMode === "live", LIVE_ACCENT, LIVE_BG)}>
                <div style={credHeader}>
                  <span style={credTitle(paypalMode === "live")}>Live credentials</span>
                  {paypalMode === "live" && <span style={activeBadge(LIVE_ACCENT)}>ACTIVE</span>}
                </div>
                <div style={labelStyle}>Client ID</div>
                <Input
                  value={liveClientId}
                  onChange={(e) => setLiveClientId(e.target.value)}
                  placeholder="Live client id"
                  style={{ marginBottom: 12 }}
                />
                <div style={labelStyle}>Secret key</div>
                <Input.Password
                  value={liveSecret}
                  onChange={(e) => setLiveSecret(e.target.value)}
                  placeholder={secretPlaceholder(liveSecretSet)}
                  autoComplete="new-password"
                />
              </div>
            </Col>
          </Row>
        </div>

        <div style={{ marginTop: 24, textAlign: "right" }}>
          <CommonButton onClick={onSave} loading={saving}>
            Save
          </CommonButton>
        </div>
      </Spin>
    </CommonDivWidget>
  );
}

export default SettingsPage;
